import React, { useEffect, useRef, useState } from 'react';
import { X } from 'react-feather';

import { TAGS } from '@/Product/constants/tags';

interface TagItem {
  tagId: number;
  tagName: string;
}

interface TagDropdownProps {
  selectedTags: TagItem[];
  onTagChange: (tagId: number, tagName: string, isSelected: boolean) => void;
  onTagRemove: (tagId: number) => void;
}

const TagDropdown: React.FC<TagDropdownProps> = ({
  selectedTags,
  onTagChange,
  onTagRemove,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isTagSelected = (tagId: number): boolean => {
    return selectedTags.some((tag) => tag.tagId === tagId);
  };

  const handleTagToggle = (tagId: number, tagName: string) => {
    const isSelected = isTagSelected(tagId);
    onTagChange(tagId, tagName, !isSelected);
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Input 버튼 */}
      <div
        role="button"
        tabIndex={0}
        className="relative mt-3 flex h-10 w-full cursor-pointer items-center rounded border border-[#CECECE] px-3 text-sm"
        onClick={handleInputClick}
      >
        <span className="text-gray-500">
          {selectedTags.length > 0 ? '' : 'Click to add tags'}
        </span>
      </div>

      {/* 드롭다운 */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded border border-[#CECECE] bg-white shadow-lg">
          {TAGS.map((tag) => (
            <div
              role="button"
              tabIndex={0}
              key={tag.tagId}
              className="flex cursor-pointer items-center px-3 py-2 hover:bg-gray-50"
              onClick={() => handleTagToggle(tag.tagId, tag.tagName)}
            >
              <input
                type="checkbox"
                checked={isTagSelected(tag.tagId)}
                onChange={() => {}} // onClick에서 처리
                className="mr-3"
                readOnly
              />
              <span className="text-sm">{tag.displayName}</span>
            </div>
          ))}
        </div>
      )}

      {/* 선택된 태그들 표시 */}
      {selectedTags.length > 0 && (
        <div className="mt-2 flex w-full flex-wrap gap-2">
          {selectedTags.map((tag) => {
            const tagInfo = TAGS.find((t) => t.tagId === tag.tagId);
            return (
              <div
                key={`${tag.tagId}-${tag.tagName}`}
                className="flex items-center gap-2 rounded bg-[#F4ECF4] px-2 py-1"
              >
                <p className="text-xs font-normal text-[#1D1D1D]">
                  {tagInfo?.displayName || tag.tagName.toUpperCase()}
                </p>
                <button
                  type="button"
                  className="cursor-pointer bg-transparent"
                  onClick={() => onTagRemove(tag.tagId)}
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TagDropdown;
